import { inject, Injectable, OnDestroy } from '@angular/core';
import { StoredStateHelper } from './stored-state.service';
import { BehaviorSubject, map, of, switchMap, tap } from 'rxjs';
import { Theme } from '../model/common';

@Injectable({
  providedIn: 'root'
})
export class ThemeService implements OnDestroy {

  private get localTheme() {
    return localStorage['theme'] as Theme | undefined
  }

  private set localTheme(theme: Theme | undefined) {
    localStorage['theme'] = theme
  }

  private stateHelper = inject(StoredStateHelper)
  private updateSubject = new BehaviorSubject<Theme|undefined>(this.localTheme)
  private readonly defaultTheme: Theme = 'light'

  readonly theme$ = this.updateSubject.pipe(
    switchMap(theme => {
      if (theme == undefined) {
        return this.stateHelper.getAppTheme<Theme>().pipe(
          tap(theme => {
            const resolvedTheme = theme ?? this.defaultTheme
            if (resolvedTheme !== this.updateSubject.value) {
              this.updateSubject.next(resolvedTheme)
            }
          })
        )
      }
      return of(theme)
    }),
    tap(theme => this.localTheme = theme),
    map(theme => theme ?? this.defaultTheme),
  )

  get theme() {
    return this.updateSubject.value
  }

  setTheme(theme: Theme) {
    if (this.updateSubject.value !== theme) {
      this.localTheme = theme
      this.stateHelper.setAppTheme(theme)
      this.updateSubject.next(theme)
    }
  }

  ngOnDestroy() {
    this.updateSubject.complete()
  }
}
